// TODO: need to cleanup and refactor this file after refactor add dataset from useConfiguration has been tested well

import { useEffect } from "react";
import { useDispatch } from "react-redux";

import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry
} from "geojson";

import { getDatasetMetadata } from "@/api/dataset/datasetApi";
import {
  GEOHASH_RESOLUTION,
  H3_RESOLUTION
} from "@/components/spatialAnalysis/spatialOutputAnalysis/data";
import { MESSAGE } from "@/constants/message";
import { buildFilterFromResponse } from "@/helpers/filterHelper";
import { getIndexOfHighestValue } from "@/lib/utils";
import {
  initialCatchmentSetting,
  setSpatialState,
  SpatialState
} from "@/store/slice/spatialSlice";
import {
  EspatialAggregationType,
  type SpatialAggregationType
} from "@/types/common";
import {
  DataSchemaType,
  type DatasetAttributeSchema,
  DatasetData,
  type DatasetDetailResponse,
  type DatasetDetailSchemaResponse,
  type DatasetFilter,
  ProportionMethodOutput,
  WeightValueEntry
} from "@/types/dataset";
import {
  AnalysisConfigurationDetail,
  DatasetConfigAttributeResponse,
  DatasetConfigResponse
} from "@/types/project";
import {
  AreaInputType,
  CatchmentDatasetConfig,
  CatchmentSettings,
  CatchmentTransport,
  ECatchmentInputBy,
  ECatchmentType,
  EOutputAnalysisType,
  EOutputCoverageType,
  EOutputGridType,
  ERoadAccessMeasurement,
  ERoadExclusions,
  IsochroneData,
  OutputGridType,
  PolygonSettingItem,
  ScoringOptionsEnum
} from "@/types/spatial";
import { generateDatasetId } from "@/utils/common";
import { createNewDataset } from "@/utils/dataset";
import {
  getDatasetAttributeScoringId,
  getPrimaryPoiAttribute
} from "@/utils/datasetAttributes";

import { area, circle } from "@turf/turf";
import { v4 as uuidv4 } from "uuid";

import { useAppFeatures } from "./useAppFeatures";

import { useMap } from "./map/core/useMapContext";
import { useDataset } from "./map/useDataset";
import useToast, { EToastType } from "./ui/useToast";

interface UseAnalysisConfigurationProps {
  configurationDetail?: AnalysisConfigurationDetail;
  onGoBack?: () => void;
}

const mapSpatialConfig = (
  configurationDetail: AnalysisConfigurationDetail,
  areaSize: number,
  isBVTDataForCatchmentEnabled?: boolean,
  isCoverageTypeEnabled?: boolean
) => {
  const outputType = configurationDetail.analysis_output?.type;
  const isProfiling = outputType === EOutputAnalysisType.PROFILING;
  const isH3 = outputType === EOutputGridType.H3;
  const isGeoHash = outputType === EOutputGridType.GEOHASH;
  const areaInput = configurationDetail.area_input;
  let areaInputOption = null;

  if (areaInput) {
    if (areaInput.adm_area_id || areaInput.adm_area_ids) {
      areaInputOption = {
        label: "Administrative Area",
        value: AreaInputType.ADMIN_AREA
      };
    } else if (areaInput.custom_catchments) {
      // When the BVT-data-for-catchment flag is enabled, Polygon is no longer
      // offered as a standalone area input — a config saved with
      // custom_catchments is surfaced as Catchment(BY_POLYGON) instead.
      areaInputOption = isBVTDataForCatchmentEnabled
        ? { label: "Catchment", value: AreaInputType.CATCHMENT }
        : { label: "Polygon", value: AreaInputType.POLYGON };
    } else {
      areaInputOption = { label: "Catchment", value: AreaInputType.CATCHMENT };
    }
  }

  let resolvedOutputType: EOutputAnalysisType | undefined;
  if (!outputType) {
    resolvedOutputType = undefined;
  } else if (isProfiling) {
    resolvedOutputType = EOutputAnalysisType.PROFILING;
  } else {
    resolvedOutputType = EOutputAnalysisType.GRID;
  }

  return {
    areaInput: areaInputOption,
    outputType: resolvedOutputType,
    gridType: configurationDetail.analysis_output?.type as OutputGridType,
    gridResolution: isGeoHash
      ? GEOHASH_RESOLUTION.find(
          (geo) =>
            geo.value ===
            configurationDetail.analysis_output?.grid_level?.toString()
        )
      : null,
    h3Resolution: isH3
      ? H3_RESOLUTION.find(
          (geo) =>
            geo.value ===
            configurationDetail.analysis_output?.grid_level?.toString()
        )
      : null,
    areaSize,
    // LOG_Z_SCORE is preserved as the persisted "advanced normalization" signal:
    // the weight-settings reducer seeds advanced mode from it and shows the radio
    // as Normalization, so the project reopens (and re-saves) as advanced.
    scoringOption:
      configurationDetail.analysis_output?.scoring_option ??
      ScoringOptionsEnum.RAW,
    intercept: String(
      configurationDetail.analysis_output?.z_score?.intercept ?? ""
    ),
    // Restore the dasimetric opt-in so an opened config reflects and re-submits
    // with the same method; an absent value means area-based (false).
    dasimetry:
      configurationDetail.analysis_output?.proportion_method ===
      ProportionMethodOutput.DASYMETRIC,
    ...(isCoverageTypeEnabled && {
      outputMode:
        configurationDetail.analysis_output?.output_mode ??
        EOutputCoverageType.STANDARD,
      customCatchmentRadius:
        configurationDetail.analysis_output?.custom_catchment?.radius_m ?? null
    })
  };
};

export const createDatasetConfig = (
  data: DatasetData,
  detail: DatasetDetailResponse,
  datasetConfig: DatasetConfigResponse
): CatchmentDatasetConfig => {
  const { id, nextIndex } = generateDatasetId(data.dataset_id, {});
  const datasetName =
    nextIndex > 1 ? `${data.name} (${nextIndex - 1})` : data.name;
  const newDataset = createNewDataset({
    dataset: detail,
    datasetId: id,
    datasetName: datasetName,
    attributesSchema: detail.schema as unknown as DatasetAttributeSchema[],
    styleConfig: {
      selectedCategory: datasetConfig.color_based,
      labelConfig: datasetConfig.label_config,
      rankConfig: {
        isEnabled: datasetConfig.label_config?.enable_ranking ?? false,
        maxRanking: datasetConfig.label_config?.max_ranking ?? 10
      }
    },
    is_global_admin_area: datasetConfig.is_global_admin_area ?? false
  });

  return {
    datasets: {
      [id]: {
        dataset: newDataset,
        rawDataset: data,
        filters: mapDatasetFilters(datasetConfig, detail.schema)
      }
    },
    visualizationOrder: [id],
    datasetIndices: {
      [data.dataset_id]: {
        counter: 1,
        usedIndices: [1]
      }
    },
    selectedDataset: null
  };
};

// Multi-dataset variant: build a single CatchmentDatasetConfig that holds
// one entry per fetched dataset. Indices/visualizationOrder are accumulated
// so each restored catchment dataset gets a unique key, mirroring the way
// `handleAddDatasetShowCatchment` builds them at runtime when the user adds
// multiple datasets via the catchment popup.
export const createMultiDatasetConfig = (
  entries: {
    data: DatasetData;
    detail: DatasetDetailResponse;
    config: DatasetConfigResponse;
  }[]
): CatchmentDatasetConfig => {
  const datasets: CatchmentDatasetConfig["datasets"] = {};
  const visualizationOrder: string[] = [];
  const datasetIndices: CatchmentDatasetConfig["datasetIndices"] = {};

  for (const { data, detail, config } of entries) {
    const { id, nextIndex } = generateDatasetId(
      data.dataset_id,
      datasetIndices
    );
    const datasetName =
      nextIndex > 1 ? `${data.name} (${nextIndex - 1})` : data.name;
    const newDataset = createNewDataset({
      dataset: detail,
      datasetId: id,
      datasetName,
      attributesSchema: detail.schema as unknown as DatasetAttributeSchema[],
      styleConfig: {
        selectedCategory: config.color_based,
        labelConfig: config.label_config,
        rankConfig: {
          isEnabled: config.label_config?.enable_ranking ?? false,
          maxRanking: config.label_config?.max_ranking ?? 10
        }
      },
      is_global_admin_area: config.is_global_admin_area ?? false
    });
    datasets[id] = {
      dataset: newDataset,
      rawDataset: data,
      filters: mapDatasetFilters(config, detail.schema)
    };
    visualizationOrder.push(id);
    const previous = datasetIndices[data.dataset_id];
    datasetIndices[data.dataset_id] = {
      counter: nextIndex,
      usedIndices: [...(previous?.usedIndices ?? []), nextIndex]
    };
  }

  return {
    datasets,
    visualizationOrder,
    datasetIndices,
    selectedDataset: null
  };
};

const mapCatchmentSettings = (
  configurationDetail: AnalysisConfigurationDetail,
  isBVTDataForCatchmentEnabled?: boolean
): CatchmentSettings | null => {
  const catchment = configurationDetail.area_input?.catchment;
  if (!catchment) {
    // Pair with mapSpatialConfig: when custom_catchments is migrated to
    // Catchment under the BVT flag, seed catchmentSettings with BY_POLYGON
    // so PolygonSelected renders the existing polygons.
    if (
      isBVTDataForCatchmentEnabled &&
      configurationDetail.area_input?.custom_catchments
    ) {
      return { catchmentInputBy: ECatchmentInputBy.BY_POLYGON };
    }
    return null;
  }

  const isDistance = catchment.metric === ERoadAccessMeasurement.DISTANCE;
  const isDuration = catchment.metric === ERoadAccessMeasurement.DURATION;
  const contours = catchment.contours?.map(String);

  // BFF normalizes responses: `datasets[]` is the single source of truth
  // for catchment dataset configs.
  const catchmentDatasets = catchment.datasets ?? [];
  const hasCatchmentDataset = catchmentDatasets.length > 0;
  // For datasetConfig restoration we still need rawDataset + detail, which
  // the BFF only provides for a single dataset today (catchmentDatasetData /
  // catchmentDatasetDetail). The remaining `datasets[1..]` carry metadata
  // only; user can re-add them in the picker if needed.
  const primaryDataset = catchmentDatasets[0];
  const isPolygonCatchmentDataset =
    primaryDataset?.spatial_aggregation_type === "polygon";

  return {
    catchmentInputBy: hasCatchmentDataset
      ? ECatchmentInputBy.BY_DATASET
      : ECatchmentInputBy.BY_LOCATION,
    radiusData:
      catchment.profile === ECatchmentType.BY_RADIUS &&
      !isPolygonCatchmentDataset
        ? contours
        : [],
    catchmentType:
      catchment.profile === ECatchmentType.BY_RADIUS
        ? ECatchmentType.BY_RADIUS
        : ECatchmentType.BY_ROAD,
    placeCoordinates: hasCatchmentDataset
      ? undefined
      : catchment.locations?.map((place) => ({
          lng: place.coordinates.longitude,
          lat: place.coordinates.latitude,
          locationName: place.name,
          id: uuidv4()
        })),
    roadAccessConfig: {
      routingType: catchment.profile as CatchmentTransport,
      measurementType: catchment.metric as ERoadAccessMeasurement,
      roadExclusions: catchment.exclusions as ERoadExclusions[],
      distances: isDistance ? contours : [],
      durations: isDuration ? contours : [],
      isochroneData:
        catchment.locations && catchment.locations?.length > 0
          ? (catchment.locations.map((location, position) => ({
              position,
              data: location.catchment_area ?? [] // safe fallback to empty array
            })) as IsochroneData[])
          : []
    },
    // Prefer the multi-dataset fetch result so every catchment dataset gets
    // restored into Redux. Falls back to the singular legacy fields when the
    // multi-dataset fetcher returned nothing (e.g. one of the calls failed).
    ...(configurationDetail.catchmentDatasetsResult?.length
      ? {
          datasetConfig: createMultiDatasetConfig(
            configurationDetail.catchmentDatasetsResult
          )
        }
      : configurationDetail.catchmentDatasetData &&
          configurationDetail.catchmentDatasetDetail &&
          primaryDataset
        ? {
            datasetConfig: createDatasetConfig(
              configurationDetail.catchmentDatasetData,
              configurationDetail.catchmentDatasetDetail,
              primaryDataset
            )
          }
        : {})
  };
};

const handleAreaInputAdmArea = (
  configurationDetail: AnalysisConfigurationDetail,
  isAdminTreeEnabled?: boolean
) => {
  const adminGeoData = configurationDetail.adminAreaData?.geoData;
  const defaultSelectedValue = {
    lv0: null,
    lv1: null,
    lv2: null,
    lv3: null,
    lv4: null
  };

  let calculatedAreaSize = 0;
  let adminAreaSettings = null;
  let selectedAreaIds: string[] = [];

  if (configurationDetail.area_input?.adm_area_id) {
    calculatedAreaSize = area(
      adminGeoData?.data[0].boundary as FeatureCollection
    );
    if (isAdminTreeEnabled) {
      // single data with admin tree UI
      adminAreaSettings = {
        lv0: configurationDetail.adminAreaData?.selectValue?.lv0 ?? null
      };
      selectedAreaIds = [configurationDetail.area_input.adm_area_id];
    } else {
      // single data with single UI
      adminAreaSettings =
        configurationDetail.adminAreaData?.selectValue ?? defaultSelectedValue;
    }
  } else if (configurationDetail.area_input?.adm_area_ids) {
    if (isAdminTreeEnabled) {
      // multiple data with admin tree UI
      calculatedAreaSize =
        adminGeoData?.data.reduce(
          (acc, curr) => acc + area(curr.boundary),
          0
        ) ?? 0;
      adminAreaSettings = {
        lv0: configurationDetail.adminAreaData?.selectValue?.lv0 ?? null
      };
      selectedAreaIds = configurationDetail.area_input.adm_area_ids;
    } else {
      // multiple data with single UI
      calculatedAreaSize = area(
        adminGeoData?.data[0].boundary as FeatureCollection
      );
      adminAreaSettings =
        configurationDetail.adminAreaData?.selectValue ?? defaultSelectedValue;
    }
  }

  return {
    calculatedAreaSize,
    adminAreaSettings,
    adminAreaSettingsData: adminGeoData ?? null,
    selectedAreaIds
  };
};

export const mapSpatialState = (
  configurationDetail: AnalysisConfigurationDetail,
  addFeature: (feature: Feature) => string | string[] | undefined,
  analysisId?: string,
  isAdminTreeEnabled?: boolean,
  isBVTDataForCatchmentEnabled?: boolean,
  isCoverageTypeEnabled?: boolean
): SpatialState => {
  let calculatedAreaSize = 0;
  let adminAreaSettings = null;
  let adminAreaSettingsData = null;
  let selectedAreaIds: string[] = [];
  const catchmentSettings = mapCatchmentSettings(
    configurationDetail,
    isBVTDataForCatchmentEnabled
  );
  const polygonSettings: Record<string, PolygonSettingItem> = {};

  // check if admin area or not
  if (
    configurationDetail.area_input?.adm_area_id ||
    configurationDetail.area_input?.adm_area_ids
  ) {
    const adminAreaResult = handleAreaInputAdmArea(
      configurationDetail,
      isAdminTreeEnabled
    );
    calculatedAreaSize = adminAreaResult.calculatedAreaSize;
    adminAreaSettings = adminAreaResult.adminAreaSettings;
    adminAreaSettingsData = adminAreaResult.adminAreaSettingsData;
    selectedAreaIds = adminAreaResult.selectedAreaIds;
  } else if (configurationDetail.area_input?.catchment) {
    /** Backward-compat: when every polygon-typed catchment dataset is
     * `available: false` (deleted/expired in Data Management) the dataset
     * fetch can no longer return rawDataset/detail, so the BY_DATASET render
     * path has nothing to draw. The geometries themselves are still preserved
     * inside `catchment.locations[].catchment_area` from save time — draw
     * them directly via `addFeature` so the map stays visually intact. We
     * intentionally do NOT touch `polygonSettings` or `catchmentInputBy` here:
     * the UI must remain in catchment-dataset-polygon mode (BY_DATASET) so
     * the polygons don't bleed into the standalone Polygon tab.
     */
    const catchmentDatasets =
      configurationDetail.area_input.catchment.datasets ?? [];
    const allPolygonCatchmentDatasetsUnavailable =
      catchmentDatasets.length > 0 &&
      catchmentDatasets.every(
        (d) => d.spatial_aggregation_type === "polygon" && d.available === false
      );
    if (allPolygonCatchmentDatasetsUnavailable) {
      configurationDetail.area_input.catchment.locations?.forEach(
        (location) => {
          location.catchment_area?.forEach((featureCollection) => {
            featureCollection.features?.forEach((feature) => {
              if (!feature.geometry) return;
              const customFeature: Feature<Geometry, GeoJsonProperties> = {
                ...feature,
                properties: {
                  ...feature.properties,
                  name: location.name
                },
                id: uuidv4()
              };
              addFeature(customFeature);
            });
          });
        }
      );
    }
    if (
      catchmentSettings?.catchmentType === ECatchmentType.BY_RADIUS &&
      catchmentSettings.radiusData &&
      catchmentSettings.radiusData.length > 0
    ) {
      const maxRadius = Math.max(
        ...catchmentSettings.radiusData.map((radius) => Number.parseInt(radius))
      );
      const areaCircle = circle([0, 0], maxRadius, {
        steps: 64,
        units: "meters"
      });
      calculatedAreaSize = area(areaCircle);
    } else if (
      catchmentSettings?.catchmentType === ECatchmentType.BY_ROAD &&
      catchmentSettings.roadAccessConfig?.isochroneData
    ) {
      const roadAccessConfig = catchmentSettings.roadAccessConfig;
      const isochroneData = roadAccessConfig.isochroneData;

      const type =
        roadAccessConfig?.measurementType === ERoadAccessMeasurement.DISTANCE
          ? (roadAccessConfig?.distances ?? [""])
          : (roadAccessConfig?.durations ?? [""]);

      const index = getIndexOfHighestValue(type ?? []);
      const highestIsochrone = isochroneData?.map(
        (data: { position: number; data: Array<FeatureCollection> }) => {
          return data.data[index];
        }
      );

      const isochroneAreas =
        highestIsochrone?.map((data) => {
          if (!data) return 0;
          return area(data);
        }) ?? [];

      calculatedAreaSize = Math.max(...isochroneAreas);
    }
  } else if (configurationDetail.area_input?.custom_catchments) {
    configurationDetail.area_input?.custom_catchments.features.forEach(
      (feature, index) => {
        const id = uuidv4();

        const customFeature: Feature<Geometry, GeoJsonProperties> = {
          ...feature,
          properties: {
            ...feature.properties
          },
          id
        };

        polygonSettings[id] = {
          polygon: customFeature,
          name: customFeature.properties?.name ?? "",
          areaSize: customFeature.properties?.area_size ?? 0,
          sequence: customFeature.properties?.sequence ?? index + 1,
          id: id
        };

        addFeature(customFeature);
      }
    );
  }

  // Properties not included here (e.g. `inputRowCounts`) are restored from
  // existing state by the `setSpatialState` action via `{ ...state, ...payload }`.
  return {
    isEdit: !!configurationDetail.id,
    currentConfigId: configurationDetail.id,
    title: configurationDetail.name,
    description: configurationDetail.description,
    adminAreaSettings,
    spatialConfig: mapSpatialConfig(
      configurationDetail,
      calculatedAreaSize,
      isBVTDataForCatchmentEnabled,
      isCoverageTypeEnabled
    ),
    polygonSettings: polygonSettings,
    polygonSettingsTemp: {},
    catchmentSettings: catchmentSettings,
    catchmentSettingsTemp: initialCatchmentSetting,
    adminAreaSettingsData: adminAreaSettingsData,
    adminAreaSettingsDataTemp: adminAreaSettingsData,
    isShowMarkerLabel:
      configurationDetail.area_input?.catchment?.datasets?.[0]?.label_config
        ?.show_label ?? false,
    latestAnalysisId: analysisId,
    overrideGlobalFilter: !configurationDetail.area_input?.is_global_admin_area,
    adminTreeSettings: {
      selectedAreaIds
    }
  };
};

const mapDatasetFilters = (
  datasetOriginal: DatasetConfigResponse,
  schema: DatasetDetailSchemaResponse[]
): DatasetFilter[] => {
  if (!datasetOriginal?.attributes?.length) return [];

  const isAttributeCodeMatch = (
    schemaCode: string | undefined | null,
    attrCode: string | undefined
  ) => {
    const normalizedSchema = schemaCode ?? "";
    const normalizedAttr = attrCode ?? "";
    return normalizedSchema === normalizedAttr;
  };

  const getPosition = (filterId: string, attributeCode: string = "") =>
    schema.find(
      (item) =>
        item.id === filterId &&
        isAttributeCodeMatch(item.attribute_code, attributeCode)
    )?.position;

  return datasetOriginal.attributes
    .map<DatasetFilter | null>((attribute) => {
      if (!attribute?.filter?.criteria) return null;

      // When saving, paired_key_attribute_id is used as the attributeId.
      // When opening, we need to find the schema item that has this paired_key_attribute_id
      // and use its actual id instead.
      const schemaItemByPairedKey = schema.find(
        (item) =>
          item.paired_key_attribute_id === attribute.id &&
          isAttributeCodeMatch(item.attribute_code, attribute.attribute_code)
      );

      // If not found by paired_key, fallback to direct id match
      const schemaItem =
        schemaItemByPairedKey ??
        schema.find(
          (item) =>
            item.id === attribute.id &&
            isAttributeCodeMatch(item.attribute_code, attribute.attribute_code)
        );

      // Use the schema item's actual id if found via paired_key lookup
      const resolvedAttributeId = schemaItemByPairedKey
        ? schemaItemByPairedKey.id
        : attribute.id;

      let attributeCode = "";
      if (attribute.attribute_code) {
        attributeCode = attribute.attribute_code;
      } else if (attribute.filter.criteria.includes("::")) {
        attributeCode = "adm_area_level_1";
      }

      return {
        // Pass the full `attribute.filter` so `buildFilterFromResponse`
        // preserves `is_multiple_adm_area` along with criteria/include_nulls —
        // mirrors `buildDatasetFilter` for the main dataset restoration path
        // and ensures the fetch payload-builder emits every selected admin
        // area ID instead of dropping all but the last one.
        ...buildFilterFromResponse(attribute.filter),
        attributeCode,
        filterId: `${resolvedAttributeId}_${getPosition(resolvedAttributeId, attributeCode)}`,
        attributeId: resolvedAttributeId,
        paired_key_attribute_id: schemaItem?.paired_key_attribute_id ?? null,
        filterLabel: schemaItem?.attribute_name ?? ""
      };
    })
    .filter((filter): filter is DatasetFilter => filter !== null);
};

/** Restore the edit-model (string) z_score from a saved attribute, if any. */
const mapWeightZScore = (
  attribute?: DatasetConfigAttributeResponse
): { log_mean: string; log_std_dev: string } | undefined => {
  const zScore = attribute?.z_score;
  if (!zScore) return undefined;
  return {
    log_mean: String(zScore.log_mean),
    log_std_dev: String(zScore.log_std_dev)
  };
};

export const mapDatasetWeights = (
  datasetOriginal: DatasetConfigResponse,
  datasetSchema: DatasetDetailSchemaResponse[]
): Record<string, WeightValueEntry> => {
  if (!datasetOriginal) return {};
  const result: Record<string, WeightValueEntry> = {};
  if (
    !datasetOriginal?.attributes?.length &&
    datasetOriginal?.spatial_aggregation_type !== "poi"
  ) {
    return result;
  }

  if (datasetOriginal?.spatial_aggregation_type === "poi") {
    const primaryPoiAttribute = getPrimaryPoiAttribute(datasetSchema);

    if (!primaryPoiAttribute) {
      const poiAttribute = datasetOriginal.attributes?.[0];
      if (!poiAttribute) return result;

      const entry: WeightValueEntry = {
        weight: String(
          poiAttribute.weight_score ?? datasetOriginal.weight ?? 0
        ),
        include_scoring: datasetOriginal.include_scoring !== false
      };

      if (
        poiAttribute.weight_negative_impact !== undefined &&
        poiAttribute.weight_negative_impact !== null
      ) {
        entry.negative_impact = poiAttribute.weight_negative_impact;
      }

      const poiZScore = mapWeightZScore(poiAttribute);
      if (poiZScore) entry.z_score = poiZScore;

      result[poiAttribute.id] = entry;
      return result;
    }

    // We iterate over `datasetSchema` below (so every schema attribute gets an
    // entry, even those missing from the response — they fall back to default
    // weight/include_scoring). The persisted weight data, however, lives on the
    // response (`datasetOriginal.attributes`). Index it by id once here so the
    // per-attribute lookup inside the loop is O(1) instead of re-scanning the
    // response array on every iteration.
    const responseAttributesById = new Map(
      datasetOriginal.attributes.map((attribute) => [attribute.id, attribute])
    );
    const isDatasetIncluded = datasetOriginal.include_scoring !== false;

    for (const attribute of datasetSchema) {
      const attributeId = getDatasetAttributeScoringId(attribute);
      const responseAttribute = responseAttributesById.get(attributeId);
      const isPrimaryAttribute = attribute === primaryPoiAttribute;
      const fallbackWeight = isPrimaryAttribute ? datasetOriginal.weight : 0;
      const entry: WeightValueEntry = {
        weight: String(responseAttribute?.weight_score ?? fallbackWeight ?? 0),
        // Only the primary POI attribute participates in scoring. Other POI
        // attributes stay present for editing/restoration, but excluded.
        include_scoring: isPrimaryAttribute && isDatasetIncluded
      };

      if (
        responseAttribute?.weight_negative_impact !== undefined &&
        responseAttribute.weight_negative_impact !== null
      ) {
        entry.negative_impact = responseAttribute.weight_negative_impact;
      }

      const responseZScore = mapWeightZScore(responseAttribute);
      if (responseZScore) entry.z_score = responseZScore;

      result[attributeId] = entry;
    }

    return result;
  }

  // Create map to hold schema data types
  const schemaDataTypeMap = new Map<string, DataSchemaType>();
  for (const schemaItem of datasetSchema) {
    schemaDataTypeMap.set(
      schemaItem.id,
      schemaItem.data_type as DataSchemaType
    );
  }

  for (const attribute of datasetOriginal.attributes) {
    const dataType = schemaDataTypeMap.get(attribute.id);

    const isDataTypeNumeric =
      dataType === DataSchemaType.INT || dataType === DataSchemaType.FLOAT;

    const isDataWeightValid =
      attribute?.weight_score !== undefined &&
      attribute?.weight_score !== null &&
      !Number.isNaN(Number(attribute.weight_score));

    if (!isDataTypeNumeric || !isDataWeightValid) {
      continue; // Skip this attribute and move to the next
    }
    const entry: WeightValueEntry = {
      weight: String(attribute.weight_score)
    };

    if (
      attribute.weight_negative_impact !== undefined &&
      attribute.weight_negative_impact !== null
    ) {
      entry.negative_impact = attribute.weight_negative_impact;
    }

    if (attribute.include_scoring === false) {
      entry.include_scoring = false;
    }

    const attributeZScore = mapWeightZScore(attribute);
    if (attributeZScore) entry.z_score = attributeZScore;

    result[attribute.id] = entry;
  }

  return result;
};

export const getDeletedDatasetsName = (datasets: DatasetConfigResponse[]) => {
  if (!datasets || datasets.length === 0) return null;
  /** Get the deleted dataset's names, should only return once per dataset if the deleted dataset is used multiple times in the configuration */
  const uniqueDatasetsById = new Set<string>();
  const names =
    datasets
      .filter(
        (dataset) =>
          !dataset.available &&
          !uniqueDatasetsById.has(dataset.id) &&
          uniqueDatasetsById.add(dataset.id)
      )
      .map((dataset) => dataset.name) ?? [];
  if (!names.length) return null;
  return names.join(", ");
};

export const useAnalysisConfiguration = ({
  configurationDetail,
  onGoBack
}: UseAnalysisConfigurationProps) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { addFeature } = useMap();
  const { handleAddDatasetAndWeightValues, handleAddDeletedDataset } =
    useDataset();
  const { isBVTDataForCatchmentEnabled, isCoverageTypeEnabled } =
    useAppFeatures();

  const getIndexOfDatasetSameId = (datasetId: string, index: number) => {
    if (!configurationDetail) {
      return 0;
    }
    const numberItemSameBefore =
      [...configurationDetail.datasets]
        .reverse()
        .slice(0, index)
        ?.filter((dataset) => dataset.id === datasetId) ?? [];
    return numberItemSameBefore?.length;
  };

  const processDatasets = async (
    configurationDetail: AnalysisConfigurationDetail
  ) => {
    const reversedDatasets = [...configurationDetail.datasets].reverse();
    const reversetDatasetsData = [
      ...configurationDetail.datasetsData
    ].reverse();
    const reversedDatasetsDetail = [
      ...configurationDetail.datasetsDetail
    ].reverse();
    // Iterate in reverse order to maintain the correct indexing
    // The first datasets need to be added last to ensure the correct dataset visual order
    for (const [index, dataset] of reversedDatasets.entries()) {
      // A configuration can use the same dataset multiple times.
      // We need to determine which instance of the dataset this is (e.g., the 1st, 2nd, etc.).
      const datasetIndex = getIndexOfDatasetSameId(dataset.id, index) + 1;
      // Convert the 1-based datasetIndex to a 0-based occurrenceIndex for array access.
      const occurrenceIndex = datasetIndex - 1;

      if (dataset.available) {
        // Since a dataset ID can be duplicated, .find() is unreliable as it only returns the first match.
        // Instead, we .filter() to get all records matching the ID...
        const matchingDatasets = reversetDatasetsData.filter(
          (datasetData) => datasetData.dataset_id === dataset.id
        );
        // ...and then use the occurrenceIndex to select the correct data record for this specific instance.
        const matchedDatasetData = matchingDatasets[occurrenceIndex];

        // The same logic applies to finding the correct metadata.
        const matchingMetadata = reversedDatasetsDetail.filter(
          (datasetMetadata) => datasetMetadata.id === dataset.id
        );
        const datasetMetadata = matchingMetadata[occurrenceIndex];

        if (matchedDatasetData && datasetMetadata) {
          let datasetSchema = datasetMetadata?.schema;
          // For H3 dataset, we need to get the aggregated schema
          if (
            datasetMetadata?.spatial_aggregation_type ===
              EspatialAggregationType.H3 &&
            datasetMetadata?.source === "bvt"
          ) {
            const aggregatedDatasetMetadata = await getDatasetMetadata(
              datasetMetadata?.id,
              {
                aggregationType: "AVG"
              }
            );
            if (aggregatedDatasetMetadata?.schema) {
              datasetSchema = aggregatedDatasetMetadata?.schema;
            }
          }

          // Use raw schema for the filter, instead of the aggregated one
          const filters = mapDatasetFilters(
            dataset,
            datasetMetadata?.schema ?? []
          );

          const weights = mapDatasetWeights(dataset, datasetMetadata?.schema);

          handleAddDatasetAndWeightValues(
            {
              ...datasetMetadata,
              display_name: dataset?.display_name ?? dataset.name
            },
            datasetIndex,
            matchedDatasetData,
            filters,
            datasetSchema as DatasetAttributeSchema[],
            { weight: dataset?.weight.toString(), attributes: weights },
            {
              colorFamily: dataset?.color_scale,
              opacity: dataset?.opacity,
              selectedCategory: dataset?.color_based,
              labelConfig: dataset?.label_config,
              colorSteps: dataset?.color_steps,
              rankConfig: {
                isEnabled: dataset.label_config?.enable_ranking ?? false,
                maxRanking: dataset.label_config?.max_ranking ?? 10
              }
            }
          );
        }
      } else if (!dataset.available) {
        // For deleted datasets, some of the properties are no longer available, hence they are assigned with arbitrary default values to conform with the data type
        const deletedDatasetMetadata: DatasetDetailResponse = {
          id: dataset.id,
          name: dataset.name ?? "",
          display_name: dataset.display_name ?? "",
          country_id: "id", // arbitrary default value
          source: "bvt", // arbitrary default value
          spatial_aggregation_type:
            dataset.spatial_aggregation_type as SpatialAggregationType,
          short_description: "",
          published: false,
          created_at: "",
          updated_at: "",
          available: dataset.available,
          total_rows: 0,
          schema: []
        };

        handleAddDeletedDataset(deletedDatasetMetadata, datasetIndex);
      }
    }
  };

  // Effect to load selected configuration detail and update Redux store
  useEffect(() => {
    if (!configurationDetail) return;

    // Clear current data and add new
    processDatasets(configurationDetail);
    // Set data for spatial config
    const spatialData = mapSpatialState(
      configurationDetail,
      addFeature,
      undefined,
      undefined,
      isBVTDataForCatchmentEnabled,
      isCoverageTypeEnabled
    );

    dispatch(setSpatialState(spatialData));
    const deletedDatasets = getDeletedDatasetsName(
      configurationDetail.datasets ?? []
    );
    if (deletedDatasets) {
      showToast({
        message: `Dataset "${deletedDatasets}" cannot be loaded, please check the dataset from data management`,
        type: EToastType.WARNING
      });
    } else {
      showToast({
        message: MESSAGE.OPEN_PROJECT_SUCCESS,
        type: EToastType.SUCCESS
      });
    }

    // Close Project List Panel
    onGoBack?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationDetail]);

  return { getIndexOfDatasetSameId, processDatasets };
};
